import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { sanitizeBidi } from "@/lib/sanitizeBidi";
import { cn } from "@/lib/utils";

const MAX_LEN = 4000;

interface NoteComposerProps {
  initialBody?: string;
  onSubmit: (body: string) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function NoteComposer({
  initialBody = "",
  onSubmit,
  onCancel,
  isSubmitting = false,
}: NoteComposerProps) {
  const { t } = useTranslation("notes");
  const [body, setBody] = useState(initialBody);
  // M12: strip Unicode bidi-override characters before validating /
  // submitting so a paste of `‮sruoy boss` doesn't persist a covert
  // direction-flip. Sanitize the trimmed value rather than `body` so
  // length tracking + the live counter still reflect what the user
  // typed; only the value crossing the persistence boundary is rewritten.
  const trimmed = sanitizeBidi(body.trim());
  const invalid = trimmed.length === 0 || body.length > MAX_LEN;
  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={t("composer.placeholder")}
        className="min-h-[80px]"
      />
      <div className="flex items-center justify-between text-xs">
        <span className={cn("text-muted-foreground", body.length > MAX_LEN && "text-destructive")}>
          {body.length}/{MAX_LEN}
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting}>
            {t("actions.cancel")}
          </Button>
          <Button
            size="sm"
            onClick={() => onSubmit(trimmed)}
            disabled={invalid || isSubmitting}
          >
            {isSubmitting ? t("actions.saving") : t("actions.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
